import * as i0 from '@angular/core';
import { PLATFORM_ID, Injectable, Inject } from '@angular/core';
import { first, map, tap, takeUntil } from 'rxjs/operators';
import { isPlatformBrowser } from '@angular/common';
import { Breakpoints, MediaBreakpoints } from '@rogers/cdk/common/enums';
import { of, BehaviorSubject, Subject, ReplaySubject } from 'rxjs';
import * as i1 from '@angular/cdk/layout';
import { By } from '@angular/platform-browser';

class WindowService {
    constructor(_platformId) {
        this._isBrowser = false;
        this._isBrowser = isPlatformBrowser(_platformId);
    }
    isBrowser() {
        return this._isBrowser;
    }
    windowRef() {
        return this._isBrowser ? window : {};
    }
    dispatchEvent(eventName, payload) {
        if (this._isBrowser) {
            const e = new CustomEvent(eventName, { detail: payload });
            document.body.dispatchEvent(e);
        }
    }
    isMobile(useInnerWidth = false) {
        if (useInnerWidth) {
            return this._isBrowser && window.innerWidth < Breakpoints.Md;
        }
        return this._isBrowser && window.outerWidth < Breakpoints.Md;
    }
    isDesktop(useInnerWidth = false) {
        if (useInnerWidth) {
            return this._isBrowser && window.innerWidth >= Breakpoints.Md;
        }
        return this._isBrowser && window.outerWidth >= Breakpoints.Md;
    }
    getPlatform() {
        if (!this._isBrowser) {
            return 'no-browser';
        }
        return this.isDesktop() ? 'desktop' : 'mobile';
    }
    getBrowsers() {
        if (!this._isBrowser) {
            return {
                // IE 11
                isIE: false,
                // Edge
                isEdge: false,
                // Chrome
                isChrome: false,
                // Safari
                isSafari: false,
                //firefox
                isFirefox: false,
            };
        }
        const agent = window.navigator.userAgent || '';
        return {
            // IE 11
            isIE: /trident\//i.test(agent),
            // Edge
            isEdge: /edge\//i.test(agent),
            // Chrome
            isChrome: /chrome\//i.test(agent),
            // Safari
            isSafari: /.*Version.*Safari.*/.test(navigator.userAgent),
            //firefox
            isFirefox: /firefox\//i.test(agent),
        };
    }
    isAppleMobileDevice() {
        const agent = (this._isBrowser && window.navigator.userAgent) || '';
        return /iPad|iPhone|iPod/.test(agent);
    }
    isIpad() {
        const agent = (this._isBrowser && window.navigator.userAgent) || '';
        return /iPad/.test(agent);
    }
    isAppleDevice() {
        const agent = (this._isBrowser && window.navigator.userAgent) || '';
        return /(Mac|iPhone|iPod|iPad)/i.test(agent);
    }
    isAndroidDevice() {
        const agent = (this._isBrowser && window.navigator.userAgent) || '';
        return /Android/.test(agent);
    }
    getSafariVersion() {
        const agent = (this._isBrowser && window.navigator.userAgent) || '';
        const match = agent.match(/Version\/([0-9\._]+).*Safari/) || [];
        return match[1];
    }
    getFirstFocusableElement(rootElement) {
        return rootElement.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')[0];
    }
    getInnerWidth() {
        return this._isBrowser ? window.innerWidth : 0;
    }
}
WindowService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: WindowService, deps: [{ token: PLATFORM_ID }], target: i0.ɵɵFactoryTarget.Injectable });
WindowService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: WindowService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: WindowService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }]; } });

// import {
//   Accordion,
//   AccordionEmitData
// } from '@rogers/cdk/common/interfaces/assembled';
// import { Video } from '@rogers/cdk/common/interfaces/assembled';
class UtilsService {
    constructor(appRef, ngZone, windowService) {
        this.appRef = appRef;
        this.ngZone = ngZone;
        this.windowService = windowService;
    }
    ucFirst(word) {
        // no CSS transform
        return word.charAt(0).toUpperCase() + word.substr(1);
    }
    objectHasKeys(obj) {
        return !!(Object.keys(obj).length || 0);
    }
    typeofIsUndefined(theVar) {
        return typeof theVar === 'undefined';
    }
    isPhoneNumber(cta) {
        if (cta.val) {
            const phoneno = /^\+?([0-9]?)[-. ]?\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/;
            return cta.val.match(phoneno) ? true : false;
        }
        return false;
    }
    getHref(cta) {
        return this.isPhoneNumber(cta) ? `tel:${cta.val}` : cta.href;
    }
    manageAccordions(accordions, eventData) {
        if (eventData.panelState === 'expanded') {
            accordions.forEach((accordion) => {
                if (eventData.accordionId === accordion.id) {
                    accordion.closeAll = false;
                }
                else {
                    accordion.closeAll = true;
                }
            });
        }
    }
    toggleVideo(videos, eventData) {
        videos.slice().forEach((video) => {
            if (video.id === eventData) {
                video.playVideo = video.playVideo ? false : true;
            }
            else {
                video.playVideo = false;
            }
        });
    }
    showWithoutExpander(data) {
        return (!data.showMore ||
            (data.showMore &&
                data.filterItems.length <= data.showMore.defaultDisplayedItems));
    }
    itemTracker(index, item) {
        if (item && item.id) {
            return item.id;
        }
        return index;
    }
    getApplicationRoot() {
        if (this.applicationRootComponent) {
            return of(this.applicationRootComponent);
        }
        return this.ngZone.onStable.pipe(first((v) => !!this.getApplicationRootComponentReference()), map((v) => this.getApplicationRootComponentReference()), tap((v) => (this.applicationRootComponent = v)));
    }
    isWebpSupported() {
        // All browsers support WebP except Safari MacOS and Safari 13.* and below on iOS.
        // https://developer.mozilla.org/en-US/docs/Web/Media/Formats/Image_types#webp
        // https://caniuse.com/webp
        const browsers = this.windowService.getBrowsers();
        return (!browsers.isSafari ||
            (this.windowService.isAppleDevice() &&
                this.windowService.getSafariVersion().substring(0, 2) >= '14'));
    }
    debounce(callback, wait) {
        let timeout;
        return (...args) => {
            const context = this;
            clearTimeout(timeout);
            timeout = setTimeout(() => callback.apply(context, args), wait);
        };
    }
    getApplicationRootComponentReference() {
        // position zero is always root component
        // https://github.com/angular/angular/issues/6446#issuecomment-175372735
        return this.appRef.components[0];
    }
    getResizeObserver() {
        if (typeof window === 'undefined') {
            return;
        }
        return window.ResizeObserver;
    }
}
UtilsService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: UtilsService, deps: [{ token: i0.ApplicationRef }, { token: i0.NgZone }, { token: WindowService }], target: i0.ɵɵFactoryTarget.Injectable });
UtilsService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: UtilsService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: UtilsService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i0.ApplicationRef }, { type: i0.NgZone }, { type: WindowService }]; } });

class AnalyticsService {
    constructor(windowService, utilService) {
        this.windowService = windowService;
        this.utilService = utilService;
    }
    injectVersion(packageJson) {
        if (this.windowService.isBrowser()) {
            this.utilService
                .getApplicationRoot()
                .pipe(first())
                .subscribe((appRoot) => {
                this.setAttribute(appRoot.location.nativeElement, packageJson);
            });
        }
    }
    setAttribute(element, packageJson) {
        element.setAttribute(`rogers-${this.getPackageName(packageJson.name)}`, packageJson.version);
    }
    getPackageName(name) {
        return name.split('/').pop();
    }
}
AnalyticsService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: AnalyticsService, deps: [{ token: WindowService }, { token: UtilsService }], target: i0.ɵɵFactoryTarget.Injectable });
AnalyticsService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: AnalyticsService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: AnalyticsService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: WindowService }, { type: UtilsService }]; } });

class LoaderService {
    constructor() {
        this.isLoading = new BehaviorSubject(false);
    }
    show() {
        this.isLoading.next(true);
    }
    hide() {
        this.isLoading.next(false);
    }
}
LoaderService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: LoaderService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
LoaderService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: LoaderService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: LoaderService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

class MediaMatchService {
    constructor(bpo) {
        this.bpo = bpo;
        this.destroyed$ = new Subject();
        this._observeMedia = new ReplaySubject();
        this.observeBreakpoints();
    }
    get isMobile() {
        return MediaBreakpoints.Mobile === this.currentMatchedMedia;
    }
    get isTablet() {
        return MediaBreakpoints.Tablet === this.currentMatchedMedia;
    }
    get isDesktop() {
        return MediaBreakpoints.Desktop === this.currentMatchedMedia;
    }
    get observeMedia() {
        return this._observeMedia;
    }
    observeBreakpoints() {
        const medias = Object.values(MediaBreakpoints);
        this.bpo
            .observe(medias)
            .pipe(takeUntil(this.destroyed$))
            .subscribe((layout) => {
            this.currentMatchedMedia = this.findMatchedMedia(layout);
            this.updateBreakpointStream(this.currentMatchedMedia);
        });
    }
    updateBreakpointStream(media) {
        this._observeMedia.next(media);
    }
    findMatchedMedia(layout) {
        return Object.keys(layout.breakpoints).find((b) => layout.breakpoints[b] === true);
    }
    ngOnDestroy() {
        this.destroyed$.next(void 0);
        this.destroyed$.complete();
    }
}
MediaMatchService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: MediaMatchService, deps: [{ token: i1.BreakpointObserver }], target: i0.ɵɵFactoryTarget.Injectable });
MediaMatchService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: MediaMatchService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: MediaMatchService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.BreakpointObserver }]; } });

class PcaAddressAutoCompleteService {
    constructor(windowService) {
        this.windowService = windowService;
        this._pcaApiReady = new BehaviorSubject(null);
        this.isPcaApiStarted = false;
    }
    load(pcaFiles) {
        const promises = [];
        pcaFiles.forEach((file) => {
            if (file.type === 'style') {
                this.loadStyles(file);
            }
            else if (file.type === 'script') {
                this.loadScript(file);
            }
        });
        return this._pcaApiReady;
    }
    loadScript(file) {
        if (this.windowService.isBrowser()) {
            if (window.pca !== undefined &&
                window.pca !== null &&
                this._pcaApiReady.value !== true) {
                this.isPcaApiStarted = true;
                this._pcaApiReady.next(true);
            }
            else if (!this.isPcaApiStarted) {
                this.isPcaApiStarted = true;
                const scriptTag = document.createElement('script');
                scriptTag.src = file.url;
                scriptTag.onload = this.onPcaAPIReady.bind(this);
                const firstScriptTag = document.getElementsByTagName('script')[0];
                firstScriptTag.parentNode.insertBefore(scriptTag, firstScriptTag);
            }
        }
    }
    onPcaAPIReady() {
        this._pcaApiReady.next(true);
    }
    loadStyles(file) {
        let elem = document.getElementById('pca-css');
        if (elem === null) {
            const styleElement = document.createElement('link');
            styleElement.rel = 'stylesheet';
            styleElement.href = file.url;
            styleElement.id = 'pca-css';
            styleElement.onload = () => {
                file.loaded = true;
            };
            document.head.appendChild(styleElement);
        }
    }
}
PcaAddressAutoCompleteService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: PcaAddressAutoCompleteService, deps: [{ token: WindowService }], target: i0.ɵɵFactoryTarget.Injectable });
PcaAddressAutoCompleteService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: PcaAddressAutoCompleteService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: PcaAddressAutoCompleteService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: WindowService }]; } });

class TestingHelperService {
    getElementByCssSelector(element, selector) {
        return element.query(By.css(selector))
            ? element.query(By.css(selector)).nativeElement
            : null;
    }
    getDebugElementsByCssSelector(element, selector) {
        return element.queryAll(By.css(selector))
            ? element.queryAll(By.css(selector))
            : null;
    }
    getDebugNodesByCssSelector(element, selector) {
        return element.queryAllNodes(By.css(selector))
            ? element.queryAllNodes(By.css(selector))
            : null;
    }
    dispatchKeyboardEvent(code, element) {
        const event = document.createEvent('KeyboardEvent');
        // Chromium Hack: filter this otherwise Safari will complain
        if (navigator.userAgent.toLowerCase().indexOf('chrome') > -1) {
            Object.defineProperty(event, 'keyCode', {
                get: function () {
                    return this.keyCodeVal;
                },
            });
            Object.defineProperty(event, 'which', {
                get: function () {
                    return this.keyCodeVal;
                },
            });
        }
        if (event.initKeyboardEvent) {
            event.initKeyboardEvent('keydown', true, true, document.defaultView, false, false, false, false, code, code);
        }
        else {
            event.initKeyEvent('keydown', true, true, document.defaultView, false, false, false, false, code, 0);
        }
        event.keyCodeVal = code;
        return element.dispatchEvent(event);
    }
}
TestingHelperService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: TestingHelperService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
TestingHelperService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: TestingHelperService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: TestingHelperService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { AnalyticsService, LoaderService, MediaMatchService, PcaAddressAutoCompleteService, TestingHelperService, UtilsService, WindowService };
